import { IndexableType } from 'dexie';
import logger from '../logger';
import { getDatabase } from './datamanager';
import { DBPeriodicSync } from './local_storage';
import recoveryServerDbManager from '../services/RecoveryServerDbManager';
import { parseMcDatabaseFilename } from '../components/mission/database-loading/DatabasesList';
import { MemoryBackup, MissionControlDatabase } from './db';
import { useRecoveryServer } from '../recoveryServerUtils';

type RecoveryServerDbFile = { filename: string; content: string };

export type DbStateLoaderDeps = {
  getDatabase: () => MissionControlDatabase;
  recoveryServerDbManager: { fetchLatestDb: () => Promise<RecoveryServerDbFile | undefined> };
  useRecoveryServer: () => boolean;
  parseMcDatabaseFilename: (filename: string) => { timestamp: number };
  logger: { log: (eventType: string, data?: any) => Promise<void> };
  periodicSyncEnabled: () => boolean;
};

const defaultDeps: DbStateLoaderDeps = {
  getDatabase,
  recoveryServerDbManager,
  useRecoveryServer,
  parseMcDatabaseFilename,
  logger,
  periodicSyncEnabled: () => DBPeriodicSync.get(),
};

export class DbStateLoader {
  private db!: MissionControlDatabase;
  private pei!: { [key: string]: any };
  // Stashed server copy from load(); used by fallbackToRecoveryServer() when a downstream
  // restoreData() fails with NotReadableError even though the probe in load() passed.
  private stashedServerCopy: RecoveryServerDbFile | undefined;

  constructor(private deps: DbStateLoaderDeps = defaultDeps) {}

  async load(pei: { [key: string]: any }) {
    this.stashedServerCopy = undefined;
    await this.log('start');
    this.db = this.deps.getDatabase();
    this.pei = pei;

    if (this.deps.periodicSyncEnabled()) {
      return await this.loadIfPeriodicSyncEnabled();
    } else {
      return await this.loadFromJsonFile();
    }
  }

  // Called by App.tsx if loadAllTables() fails with NotReadableError despite the probe.
  // Writes the previously-stashed recovery-server copy as a fresh MemoryBackup row so
  // the next loadAllTables() retry reads from known-good data. Single-use per load().
  async fallbackToRecoveryServer(): Promise<IndexableType | undefined> {
    if (!this.stashedServerCopy) {
      await this.log('fallback: no stashed recovery-server copy available');

      return undefined;
    }

    const serverCopy = this.stashedServerCopy;
    this.stashedServerCopy = undefined;

    const latest = await this.db.MemoryBackup.orderBy('timestamp').last();

    if (latest) {
      await this.log('fallback: deleting broken local memory backup', { timestamp: latest.timestamp });
      await this.db.MemoryBackup.delete(latest.timestamp);
    }

    await this.log('fallback: writing recovery-server copy as fresh memory backup');

    return await this.loadFromDbFromRecoveryServer(serverCopy);
  }

  private async loadIfPeriodicSyncEnabled() {
    const justResetApp = sessionStorage.getItem('just_reset_app');
    await this.log(`DBPeriodicSync is ON - just reset app - ${justResetApp ? 'yes' : 'no'}`);

    if (justResetApp) {
      await this.log('just reset app, loading from PEI');

      sessionStorage.removeItem('just_reset_app');

      return await this.loadFromPei();
    }

    const localMemoryBackup = await this.db.MemoryBackup.orderBy('timestamp').last();
    const latestDbFromRecoveryServer = this.deps.useRecoveryServer()
      ? await this.deps.recoveryServerDbManager.fetchLatestDb()
      : undefined;

    if (!localMemoryBackup && !latestDbFromRecoveryServer) {
      await this.log('no local memory backup and no recovery server DB found, loading from PEI');

      return await this.loadFromPei();
    }

    if (localMemoryBackup && !latestDbFromRecoveryServer) {
      const localUsable = await this.probeLocalReadable(localMemoryBackup);

      if (!localUsable) {
        await this.log('local unreadable and no recovery-server fallback, loading from PEI');

        return await this.loadFromPei();
      }

      this.log('found only local memory backup, using it', {
        timestamp: localMemoryBackup.timestamp,
        humanReadable: new Date(localMemoryBackup.timestamp).toISOString(),
      });

      return localMemoryBackup;
    }

    if (!localMemoryBackup && latestDbFromRecoveryServer) {
      const { timestamp: latestDbFromRecoveryServerTimestamp } = this.deps.parseMcDatabaseFilename(
        latestDbFromRecoveryServer.filename,
      );

      this.log('found only recovery server DB, using it', {
        filename: latestDbFromRecoveryServer.filename,
        recoveryServerTs: latestDbFromRecoveryServerTimestamp,
        recoveryServerTsHuman: new Date(latestDbFromRecoveryServerTimestamp).toISOString(),
      });

      return await this.loadFromDbFromRecoveryServer(latestDbFromRecoveryServer);
    }

    // At this point, we know we have both lastMemoryBackup and latestDbFromRecoveryServer.
    // However, typescript can't infer that, so we do a runtime check.
    if (!localMemoryBackup || !latestDbFromRecoveryServer) {
      throw new Error('Unreachable code reached in DbStateLoader');
    }

    const { timestamp: latestDbFromRecoveryServerTimestamp } = this.deps.parseMcDatabaseFilename(
      latestDbFromRecoveryServer.filename,
    );

    await this.log('found both local memory backup and recovery server DB', {
      localTs: localMemoryBackup.timestamp,
      localTsHuman: new Date(localMemoryBackup.timestamp).toISOString(),
      recoveryServerTs: latestDbFromRecoveryServerTimestamp,
      recoveryServerTsHuman: new Date(latestDbFromRecoveryServerTimestamp).toISOString(),
    });

    // If our latest recovery server DB is newer than our local memory backup, use it
    if (localMemoryBackup.timestamp < latestDbFromRecoveryServerTimestamp) {
      await this.log('recovery server DB is newer than our local memory backup - using it');

      return await this.loadFromDbFromRecoveryServer(latestDbFromRecoveryServer);
    }

    // Local is newer — probe readability before committing. If the on-disk blob is dead we
    // fall through to the server copy despite the older timestamp.
    const localUsable = await this.probeLocalReadable(localMemoryBackup);

    if (!localUsable) {
      await this.log('local newer but unreadable - falling through to recovery server DB');

      return await this.loadFromDbFromRecoveryServer(latestDbFromRecoveryServer);
    }

    // Happy path: local is newer and readable. Stash the server copy as a fallback in case
    // a later restoreData() call still trips on the same blob (e.g. mid-read corruption).
    this.stashedServerCopy = latestDbFromRecoveryServer;
    await this.log('local memory backup is newer than recovery server DB and USABLE - using it');

    return localMemoryBackup;
  }

  // Cheap readability check. Reading even 1 byte of a Blob forces Chrome to open the
  // backing file; if that file is missing/permission-broken we get NotReadableError
  // without paying the full-read cost. Any probe failure — whether a known-corruption
  // signal or an unknown error — demotes local so the server copy takes over. We only
  // *delete* the row on NotReadableError though: that's confirmed corruption.
  // Unknown errors might be transient (OOM, IDB hiccup) — leaving the row lets a
  // future boot retry it if the underlying condition clears.
  private async probeLocalReadable(backup: MemoryBackup): Promise<boolean> {
    try {
      await backup.data.slice(0, 1).text();

      return true;
    } catch (err: any) {
      const confirmedCorruption = err?.name === 'NotReadableError';

      await this.log('local memory backup probe failed, demoting to unreadable', {
        timestamp: backup.timestamp,
        err,
        deletedPoisonedRow: confirmedCorruption,
      });

      if (confirmedCorruption) {
        await this.db.MemoryBackup.delete(backup.timestamp);
      }

      return false;
    }
  }

  private async loadFromDbFromRecoveryServer(dbFromRecoveryServer: RecoveryServerDbFile) {
    const { content } = dbFromRecoveryServer;

    return await this.db.MemoryBackup.put({
      timestamp: Date.now(),
      data: new Blob([content]),
      name: 'auto',
      checksum: '',
    });
  }

  private async loadFromPei() {
    return await this.db.MemoryBackup.put({
      timestamp: Date.now(),
      data: new Blob([JSON.stringify(this.pei)]),
      name: 'auto',
      checksum: '',
    });
  }

  private async loadFromJsonFile() {
    await this.log('load from JSON file');

    const promiseGenerators: (() => Promise<IndexableType>)[] = [];

    for (const tableName of Object.keys(this.pei)) {
      const table = this.db.table(tableName);

      this.pei[tableName].forEach(async (inst: any): Promise<any> => {
        promiseGenerators.push(async () => {
          const obj = await table.get(parseInt(inst._instance_id));

          if (!obj) {
            return await table.put(inst);
          }

          return obj;
        });
      });
    }

    return await Promise.all(promiseGenerators.map((gen) => gen()));
  }

  private async log(message: string, ...optionalParams: any[]) {
    console.log(`DB_STATE_LOAD - ${message}`, ...optionalParams);

    await this.deps.logger.log(`DB_STATE_LOAD - ${message}`, ...optionalParams);
  }
}

const dbStateLoader = new DbStateLoader();

export default dbStateLoader;
